"use client";
import React, { useEffect, useRef, useState } from "react";

function randomBetween(a: number, b: number) {
  return a + Math.random() * (b - a);
}

type Circle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  color: string;
  mass: number; // Add mass property
};

const RESTITUTION = 1.0; // Perfectly elastic for maximum bounciness

function resolveCollision(a: Circle, b: Circle) {
  // Calculate the normal vector
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist === 0) return;

  // Normalized normal and tangent vectors
  const nx = dx / dist;
  const ny = dy / dist;
  const tx = -ny;
  const ty = nx;

  // Project velocities onto the normal and tangent directions
  const va_n = a.vx * nx + a.vy * ny;
  const vb_n = b.vx * nx + b.vy * ny;
  const va_t = a.vx * tx + a.vy * ty;
  const vb_t = b.vx * tx + b.vy * ty;

  // Only exchange normal components, tangential stays the same
  const m1 = a.mass;
  const m2 = b.mass;

  // 1D elastic collision equations for normal components
  const va_n_after = (va_n * (m1 - m2) + 2 * m2 * vb_n) / (m1 + m2);
  const vb_n_after = (vb_n * (m2 - m1) + 2 * m1 * va_n) / (m1 + m2);

  // Apply restitution for bounciness
  const va_n_final = va_n_after * RESTITUTION;
  const vb_n_final = vb_n_after * RESTITUTION;

  // Convert scalar normal/tangent velocities back to vectors
  a.vx = va_n_final * nx + va_t * tx;
  a.vy = va_n_final * ny + va_t * ty;
  b.vx = vb_n_final * nx + vb_t * tx;
  b.vy = vb_n_final * ny + vb_t * ty;
}

export default function Background({
  children,
}: {
  children: React.ReactNode;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener("resize", handleResize);

    // Use brand palette from CSS variables
    const root = getComputedStyle(document.documentElement);
    const brandPalette = [
      root.getPropertyValue("--primary").trim() || "#5b21b6",
      root.getPropertyValue("--primary-bright").trim() || "#7e3af2",
      root.getPropertyValue("--accent-rose").trim() || "#ff6fa5",
      root.getPropertyValue("--accent").trim() || "#ff9d5c",
    ];

    // Create circles
    const circles: Circle[] = [];
    const N = 18;
    for (let i = 0; i < N; ++i) {
      let tries = 0;
      let circle: Circle;
      do {
        const r = randomBetween(28, 48);
        circle = {
          x: randomBetween(60, width - 60),
          y: randomBetween(60, height - 60),
          vx: randomBetween(-1.5, 1.5),
          vy: randomBetween(-1.5, 1.5),
          r,
          color:
            brandPalette[Math.floor(randomBetween(0, brandPalette.length))],
          mass: Math.PI * r * r, // Mass proportional to area
        };
        tries++;
        // Avoid initial overlap
      } while (
        circles.some(
          (c) => Math.hypot(c.x - circle.x, c.y - circle.y) < c.r + circle.r + 8
        ) &&
        tries < 100
      );
      circles.push(circle);
    }

    function animate() {
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);

      // Move and bounce off walls
      for (const c of circles) {
        c.x += c.vx;
        c.y += c.vy;

        // Wall bounce with restitution
        if (c.x - c.r < 0) {
          c.x = c.r;
          c.vx = -c.vx * RESTITUTION;
        }
        if (c.x + c.r > width) {
          c.x = width - c.r;
          c.vx = -c.vx * RESTITUTION;
        }
        if (c.y - c.r < 0) {
          c.y = c.r;
          c.vy = -c.vy * RESTITUTION;
        }
        if (c.y + c.r > height) {
          c.y = height - c.r;
          c.vy = -c.vy * RESTITUTION;
        }
      }

      // Handle collisions
      for (let i = 0; i < circles.length; ++i) {
        for (let j = i + 1; j < circles.length; ++j) {
          const a = circles[i];
          const b = circles[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.hypot(dx, dy);
          if (dist < a.r + b.r) {
            // Separate them based on their mass
            const overlap = a.r + b.r - dist;
            const nx = dx / dist;
            const ny = dy / dist;
            const totalMass = a.mass + b.mass;
            a.x -= nx * overlap * (b.mass / totalMass);
            a.y -= ny * overlap * (b.mass / totalMass);
            b.x += nx * overlap * (a.mass / totalMass);
            b.y += ny * overlap * (a.mass / totalMass);
            resolveCollision(a, b);
          }
        }
      }

      // Draw circles
      for (const c of circles) {
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.r, 0, 2 * Math.PI);
        ctx.fillStyle = c.color;
        ctx.globalAlpha = 0.35;
        ctx.shadowColor = c.color;
        ctx.shadowBlur = 28;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
      }

      animationRef.current = requestAnimationFrame(animate);
    }

    animate();
    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isClient]);

  return (
    <>
      {isClient ? (
        <canvas
          ref={canvasRef}
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            zIndex: -1,
            pointerEvents: "none",
            background: "transparent",
          }}
        />
      ) : (
        <div
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            zIndex: -1,
            pointerEvents: "none",
            background: "transparent",
          }}
        />
      )}
      {children}
    </>
  );
}
